import { useEffect, useState, useMemo } from 'react';
import { api, formatMoney } from '@/lib/api';
import { useCategories } from '@/context/CategoriesContext';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Percent, DollarSign, TrendingDown, Calculator, Info, Tag, Zap } from 'lucide-react';

export default function DashboardComisiones() {
    const [cfg, setCfg] = useState(null);
    const cats = useCategories();

    // Quoter state
    const [price, setPrice] = useState('500');
    const [selectedCat, setSelectedCat] = useState('__none__');
    const [adType, setAdType] = useState('prime');

    useEffect(() => {
        api.get('/commissions').then(({ data }) => setCfg(data)).catch(() => {});
    }, []);

    const commissionForCat = useMemo(() => {
        if (!cfg) return 0;
        if (adType === 'gratuita') return 0;
        const catMap = Object.fromEntries((cfg.categories || []).map(c => [c.slug, c.commission_pct]));
        if (selectedCat === '__none__') return cfg.default_commission_pct ?? 10;
        return catMap[selectedCat] ?? cfg.default_commission_pct ?? 10;
    }, [cfg, selectedCat, adType]);

    const fixedCost = useMemo(() => {
        if (!cfg || adType === 'gratuita') return 0;
        const numPrice = parseFloat(price) || 0;
        const tiers = cfg.fixed_cost_tiers || [];
        for (const tier of tiers) {
            const above = numPrice >= (tier.min_price ?? 0);
            const below = tier.max_price === null || tier.max_price === undefined || numPrice < tier.max_price;
            if (above && below) return tier.fixed_cost ?? 0;
        }
        return 0;
    }, [cfg, price, adType]);

    const numPrice = parseFloat(price) || 0;
    const commissionAmount = adType === 'gratuita' ? 0 : Math.round(numPrice * commissionForCat / 100 * 100) / 100;
    const totalDeducted = commissionAmount + fixedCost;
    const sellerNet = Math.max(0, numPrice - totalDeducted);
    const buyerPays = numPrice + commissionAmount; // commission added to buyer price

    if (!cfg) return <div className="text-sm text-muted-foreground py-8">Cargando…</div>;

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">Comisiones</h1>
                <p className="text-sm text-muted-foreground">Conoce las comisiones que aplican a tus publicaciones y cuánto ganarás por venta.</p>
            </div>

            {/* Listing types summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border bg-card p-4 space-y-2">
                    <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Zap className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div>
                            <div className="font-semibold text-sm">Publicación Gratuita</div>
                            <Badge variant="outline" className="border-emerald-500 text-emerald-700 text-[10px] mt-0.5">0% comisión</Badge>
                        </div>
                    </div>
                    <ul className="text-xs text-muted-foreground space-y-0.5 pl-1">
                        <li>• Duración: 90 días por publicación</li>
                        <li>• Sin comisión por venta</li>
                        <li>• El comprador paga exactamente el precio del producto</li>
                        <li>• Disponible en todos los planes</li>
                    </ul>
                </div>
                <div className="rounded-2xl border bg-card p-4 space-y-2 border-amber-300/60">
                    <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                            <TrendingDown className="h-4 w-4 text-amber-600" />
                        </div>
                        <div>
                            <div className="font-semibold text-sm">Publicación Prime</div>
                            <Badge className="bg-amber-500 text-white text-[10px] mt-0.5">Mayor visibilidad</Badge>
                        </div>
                    </div>
                    <ul className="text-xs text-muted-foreground space-y-0.5 pl-1">
                        <li>• Duración: <strong>ilimitada</strong></li>
                        <li>• Comisión según categoría del producto</li>
                        <li>• Prioridad en resultados de búsqueda</li>
                        <li>• Requiere plan con Prime habilitado</li>
                    </ul>
                </div>
            </div>

            {/* Interactive quoter */}
            <div className="rounded-2xl border bg-card overflow-hidden">
                <div className="px-5 py-4 border-b bg-gradient-to-r from-slate-900 to-neutral-800 text-white flex items-center gap-2">
                    <Calculator className="h-5 w-5" />
                    <div>
                        <div className="font-semibold">Cotizador de comisiones</div>
                        <div className="text-xs text-white/60">Simula cuánto ganarás por tu venta</div>
                    </div>
                </div>
                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <Label className="text-xs">Precio de venta (MXN)</Label>
                            <Input type="number" min="1" value={price}
                                onChange={e => setPrice(e.target.value)}
                                placeholder="Ej. 500" className="mt-1" />
                        </div>
                        <div>
                            <Label className="text-xs">Categoría</Label>
                            <Select value={selectedCat} onValueChange={setSelectedCat}>
                                <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona categoría" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">Sin categoría (comisión base)</SelectItem>
                                    {cats.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Tipo de publicación</Label>
                            <Select value={adType} onValueChange={setAdType}>
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="gratuita">Gratuita (0% comisión)</SelectItem>
                                    <SelectItem value="prime">Prime (comisión por categoría)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Breakdown */}
                    {numPrice > 0 && (
                        <div className="rounded-xl border divide-y text-sm">
                            <div className="px-4 py-2.5 flex justify-between">
                                <span className="text-muted-foreground">Precio del producto</span>
                                <span className="font-medium tabular-nums">{formatMoney(numPrice, 'MXN')}</span>
                            </div>
                            {adType === 'prime' && (
                                <>
                                    <div className="px-4 py-2.5 flex justify-between">
                                        <span className="text-muted-foreground">
                                            Comisión plataforma ({commissionForCat}%)
                                        </span>
                                        <span className="tabular-nums text-red-600">− {formatMoney(commissionAmount, 'MXN')}</span>
                                    </div>
                                    {fixedCost > 0 && (
                                        <div className="px-4 py-2.5 flex justify-between">
                                            <span className="text-muted-foreground">Costo fijo por venta</span>
                                            <span className="tabular-nums text-red-600">− {formatMoney(fixedCost, 'MXN')}</span>
                                        </div>
                                    )}
                                </>
                            )}
                            <div className="px-4 py-3 flex justify-between bg-emerald-50 rounded-b-xl">
                                <span className="font-semibold text-emerald-700">Tú recibes</span>
                                <span className="font-bold text-emerald-700 text-base tabular-nums">{formatMoney(sellerNet, 'MXN')}</span>
                            </div>
                        </div>
                    )}
                    {adType === 'prime' && numPrice > 0 && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Info className="h-3.5 w-3.5 shrink-0" />
                            El comprador paga <strong>{formatMoney(buyerPays, 'MXN')}</strong> (precio + comisión). La comisión la cubre el comprador, no tú.
                        </p>
                    )}
                </div>
            </div>

            {/* Commission table by category */}
            {cfg.categories && cfg.categories.length > 0 && (
                <div className="rounded-2xl border bg-card overflow-hidden">
                    <div className="px-5 py-4 border-b flex items-center gap-2">
                        <Tag className="h-4 w-4 text-red-600" />
                        <div className="font-semibold text-sm">Comisiones por categoría (publicaciones Prime)</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b bg-neutral-50 text-xs text-muted-foreground">
                                    <th className="text-left px-5 py-2">Categoría</th>
                                    <th className="text-right px-5 py-2">Comisión</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cfg.categories.filter(c => c.commission_pct > 0).map(c => (
                                    <tr key={c.slug} className="border-b last:border-0 hover:bg-neutral-50/50">
                                        <td className="px-5 py-2.5 font-medium">{c.name}</td>
                                        <td className="px-5 py-2.5 text-right tabular-nums font-semibold">{c.commission_pct}%</td>
                                    </tr>
                                ))}
                                <tr className="bg-neutral-50 border-t">
                                    <td className="px-5 py-2.5 text-muted-foreground text-xs italic">Otras categorías</td>
                                    <td className="px-5 py-2.5 text-right tabular-nums text-xs text-muted-foreground">{cfg.default_commission_pct}% (base)</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Fixed cost tiers */}
            {cfg.fixed_cost_tiers && cfg.fixed_cost_tiers.length > 0 && (
                <div className="rounded-2xl border bg-card overflow-hidden">
                    <div className="px-5 py-4 border-b flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-red-600" />
                        <div className="font-semibold text-sm">Costos fijos adicionales (publicaciones Prime)</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b bg-neutral-50 text-xs text-muted-foreground">
                                    <th className="text-left px-5 py-2">Rango de precio</th>
                                    <th className="text-right px-5 py-2">Costo fijo por unidad</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cfg.fixed_cost_tiers.map((tier, i) => (
                                    <tr key={i} className="border-b last:border-0 hover:bg-neutral-50/50">
                                        <td className="px-5 py-2.5">{tier.label}</td>
                                        <td className="px-5 py-2.5 text-right tabular-nums font-semibold">
                                            {tier.fixed_cost > 0 ? formatMoney(tier.fixed_cost, 'MXN') : <span className="text-emerald-600">Sin costo</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
